import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, X, Eye, EyeOff, Globe, Mail, Briefcase, Image as ImageIcon, Palette, ExternalLink, Send, Trash2, Plus, Lock, Inbox, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const SERVICE_PRESETS = [
  "Substack growth",
  "Newsletter strategy",
  "Brand voice development",
  "Ghostwriting",
  "Podcast & multi-platform repurposing",
  "Editorial calendar management",
  "Audience research & ICP",
  "Conversion-focused copywriting",
  "Lead magnets & funnels",
];

export default function MogulMode() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [slug, setSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null });
  const [working, setWorking] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.billingMe().then(setMe).catch(() => {});
    api.getMyMogulProfile().then((p) => {
      setProfile(p);
      setSlug(p.slug || "");
    }).catch(() => {});
    api.listMogulLeads().then(setLeads).catch(() => {});
  }, []);

  // Live slug check (debounced)
  useEffect(() => {
    if (!slug || slug === profile?.slug) return;
    setSlugStatus({ checking: true, available: null });
    const t = setTimeout(() => {
      api.checkMogulSlug(slug).then((r) => setSlugStatus({ checking: false, available: r.available }));
    }, 400);
    return () => clearTimeout(t);
  }, [slug, profile?.slug]);

  if (!profile) return <div className="text-sm mono uppercase text-[#A1A1AA]">loading…</div>;

  // Gate
  const tier = me?.tier;
  const tierOk = me?.is_creator || tier === "mogul" || tier === "founding";

  const update = (patch) => setProfile({ ...profile, ...patch });

  const save = async (extra = {}) => {
    setWorking(true);
    try {
      const payload = { ...profile, ...extra };
      // Only send fields that exist
      delete payload.exists;
      delete payload.user_id;
      delete payload.updated_at;
      if (payload.slug !== profile.slug && slugStatus.available === false) {
        toast.error("That slug is already taken");
        setWorking(false);
        return;
      }
      const next = await api.saveMogulProfile(payload);
      setProfile({ ...next, exists: true });
      toast.success("Saved.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Save failed");
    } finally { setWorking(false); }
  };

  const toggleService = (svc) => {
    const cur = profile.services || [];
    const next = cur.includes(svc) ? cur.filter((s) => s !== svc) : [...cur, svc];
    update({ services: next });
  };

  const publicUrl = profile.slug && profile.is_published
    ? `${window.location.origin}/m/${profile.slug}`
    : null;

  return (
    <div className="max-w-5xl space-y-8" data-testid="mogul-mode-page">
      <div>
        <div className="text-eyebrow mb-2 flex items-center gap-2"><Crown size={12}/> Mogul Mode</div>
        <h1 className="font-display font-black tracking-tighter text-5xl">Your agency. Your terms. Zero compromise.</h1>
        <p className="text-sm text-[#52525B] mt-3 max-w-2xl">A standalone public page that positions you as the operator running the show. Visitors see your work, your brands, your contact. No Vitality branding. Plug it into your own website, LinkedIn, business card.</p>
      </div>

      {!tierOk && (
        <div className="border-2 border-black p-6 bg-white" data-testid="mogul-paywall">
          <div className="flex items-center gap-2 mb-2"><Lock size={16}/><div className="text-eyebrow">Mogul tier required</div></div>
          <h3 className="font-display font-black tracking-tighter text-3xl mb-2">Mogul Mode is part of Mogul & Founding tiers.</h3>
          <p className="text-sm text-[#52525B] mb-4">$399/mo unlocks the public agency page, unlimited brands, cross-pollination, and 40-50% affiliate commissions.</p>
          <button onClick={() => navigate("/pricing")} className="bg-black text-white px-5 py-3 text-sm hover:bg-[#002FA7]">See plans →</button>
        </div>
      )}

      {tierOk && (
        <>
          {/* PUBLISH BAR */}
          <div className="border-2 border-black p-5 flex items-center justify-between flex-wrap gap-3" data-testid="publish-bar">
            <div className="flex items-center gap-3">
              {profile.is_published ? (
                <>
                  <div className="w-3 h-3 bg-[#059669] rounded-full"/>
                  <div>
                    <div className="text-sm font-semibold">Live</div>
                    {publicUrl && (
                      <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs mono text-[#52525B] hover:text-black flex items-center gap-1" data-testid="public-url-link">
                        {publicUrl} <ExternalLink size={10}/>
                      </a>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 bg-[#A1A1AA] rounded-full"/>
                  <div>
                    <div className="text-sm font-semibold">Draft</div>
                    <div className="text-xs text-[#52525B]">Set a slug and publish to make this live.</div>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {publicUrl && (
                <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("URL copied"); }} className="border border-black px-4 py-2 text-sm hover:bg-black hover:text-white" data-testid="copy-public-url-button">
                  Copy URL
                </button>
              )}
              <button
                onClick={() => save({ is_published: !profile.is_published })}
                disabled={working || !profile.slug || slugStatus.available === false}
                data-testid="publish-toggle-button"
                className={`px-5 py-2 text-sm ${profile.is_published ? "bg-white text-black border-2 border-black hover:bg-[#E11D48] hover:border-[#E11D48] hover:text-white" : "bg-black text-white hover:bg-[#F59E0B] hover:text-black"}`}
              >
                {profile.is_published ? "Unpublish" : "Publish live"}
              </button>
            </div>
          </div>

          {/* IDENTITY */}
          <div className="border border-black p-6 space-y-4" data-testid="identity-block">
            <div className="text-eyebrow flex items-center gap-2"><Globe size={12}/> Identity</div>

            <div>
              <label className="text-eyebrow mb-1 block">Your handle (the URL)</label>
              <div className="flex items-stretch border-2 border-black">
                <span className="px-3 py-2 bg-[#FAFAFA] text-sm mono text-[#52525B] border-r border-black flex items-center">/m/</span>
                <input
                  value={slug}
                  onChange={(e) => { const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""); setSlug(v); update({ slug: v }); }}
                  placeholder="your-handle"
                  className="flex-1 p-2 outline-none mono text-sm"
                  data-testid="slug-input"
                />
                {slug && (
                  <span className="px-3 flex items-center text-xs mono border-l border-black bg-[#FAFAFA]">
                    {slugStatus.checking ? "…" : slugStatus.available === false ? <span className="text-[#E11D48]">taken</span> : slugStatus.available === true ? <span className="text-[#059669]">available</span> : ""}
                  </span>
                )}
              </div>
              <div className="text-xs text-[#52525B] mt-2">3-30 chars · lowercase + numbers + dashes</div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-eyebrow mb-1 block">Agency / studio name</label>
                <input value={profile.agency_name || ""} onChange={(e) => update({ agency_name: e.target.value })} placeholder="Acme Editorial" data-testid="agency-name-input" className="w-full border-2 border-black p-2 text-base"/>
              </div>
              <div>
                <label className="text-eyebrow mb-1 block">Tagline</label>
                <input value={profile.tagline || ""} onChange={(e) => update({ tagline: e.target.value })} placeholder="We turn newsletters into media empires." data-testid="tagline-input" className="w-full border-2 border-black p-2 text-base"/>
              </div>
            </div>

            <div>
              <label className="text-eyebrow mb-1 block">Bio / story (markdown OK)</label>
              <textarea value={profile.bio || ""} onChange={(e) => update({ bio: e.target.value })} rows={4} placeholder="Three sentences about you, why you do this, who you serve." data-testid="bio-input" className="w-full border-2 border-black p-2 text-sm leading-relaxed"/>
            </div>

            <DraftCopyHelper profile={profile} onPick={(t, b) => update({ tagline: t, bio: b })}/>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-eyebrow mb-1 flex items-center gap-2"><ImageIcon size={11}/> Headshot URL</label>
                <input value={profile.headshot_url || ""} onChange={(e) => update({ headshot_url: e.target.value })} placeholder="https://…" data-testid="headshot-input" className="w-full border-2 border-black p-2 text-sm mono"/>
              </div>
              <div>
                <label className="text-eyebrow mb-1 flex items-center gap-2"><Palette size={11}/> Accent color (#RRGGBB)</label>
                <div className="flex gap-2">
                  <input value={profile.accent_color || "#002FA7"} onChange={(e) => update({ accent_color: e.target.value })} data-testid="accent-color-input" className="flex-1 border-2 border-black p-2 text-sm mono"/>
                  <div className="w-12 border-2 border-black" style={{ backgroundColor: profile.accent_color || "#002FA7" }}/>
                </div>
              </div>
            </div>
          </div>

          {/* SERVICES */}
          <div className="border border-black p-6 space-y-3" data-testid="services-block">
            <div className="text-eyebrow flex items-center gap-2"><Briefcase size={12}/> Services you offer</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {SERVICE_PRESETS.map((s) => {
                const active = (profile.services || []).includes(s);
                return (
                  <button key={s} onClick={() => toggleService(s)} data-testid={`service-${s.toLowerCase().replaceAll(" ","-")}`}
                          className={`text-sm border-2 p-2 text-left transition-colors ${active ? "border-black bg-[#FFFBEB]" : "border-[#E2E4E9] hover:border-black"}`}>
                    {active && <Check size={12} className="inline mr-1"/>}
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* CONTACT */}
          <div className="border border-black p-6 space-y-3" data-testid="contact-block">
            <div className="text-eyebrow flex items-center gap-2"><Mail size={12}/> Contact</div>
            <div className="grid md:grid-cols-2 gap-3">
              <input value={profile.contact_email || ""} onChange={(e) => update({ contact_email: e.target.value })} placeholder="hello@yourdomain.com" data-testid="contact-email-input" className="border-2 border-black p-2 text-sm mono"/>
              <input value={profile.intake_link || ""} onChange={(e) => update({ intake_link: e.target.value })} placeholder="Cal.com / Stripe invoice link / your website" data-testid="intake-link-input" className="border-2 border-black p-2 text-sm mono"/>
            </div>
            <p className="text-xs text-[#52525B]">Visitors submit your contact form; inquiries email to this address. If you set a Cal.com or Stripe invoice link, we show a "Work with me" button that goes there directly.</p>
          </div>

          {/* CUSTOM DOMAIN */}
          <div className="border border-black p-6 space-y-3" data-testid="custom-domain-block">
            <div className="text-eyebrow flex items-center gap-2"><Globe size={12}/> Custom domain (white-label)</div>
            <p className="text-xs text-[#52525B] leading-relaxed">
              Host your agency page on your own domain. After saving, CNAME <strong className="mono">{(profile.custom_domain || "yourdomain.com")}</strong> to <strong className="mono">{window.location.host}</strong>.
              We auto-detect the incoming domain and serve your page — no Vitality URL ever showing.
            </p>
            <input value={profile.custom_domain || ""} onChange={(e) => update({ custom_domain: e.target.value.toLowerCase().trim() })} placeholder="acme.com or studio.acme.com" data-testid="custom-domain-input" className="w-full border-2 border-black p-2 text-sm mono"/>
          </div>

          {/* DISPLAY OPTIONS */}
          <div className="border border-black p-6 space-y-3" data-testid="display-options-block">
            <div className="text-eyebrow">Display options</div>
            <Toggle label="Show brand showcase" hint="Display the brands you've marked 'public showcase' on your individual brand settings." checked={!!profile.show_brand_showcase} onChange={(v) => update({ show_brand_showcase: v })} testid="toggle-brand-showcase"/>
            <Toggle label="Show growth stats" hint="Reveals your brand & post counts. Pure social proof." checked={!!profile.show_stats} onChange={(v) => update({ show_stats: v })} testid="toggle-stats"/>
            <Toggle label="Show 'Powered by Vitality' footer" hint="Off by default for dignity. Turn on to give a quiet credit." checked={!!profile.show_powered_by} onChange={(v) => update({ show_powered_by: v })} testid="toggle-powered-by"/>
          </div>

          <div className="flex justify-end">
            <button onClick={() => save()} disabled={working} data-testid="save-mogul-button"
                    className="bg-black text-white px-6 py-3 text-sm hover:bg-[#002FA7] disabled:opacity-50">
              {working ? "Saving…" : "Save changes"}
            </button>
          </div>

          {/* LEADS INBOX */}
          <div className="border-2 border-black p-6" data-testid="leads-inbox">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-eyebrow mb-1 flex items-center gap-2"><Inbox size={12}/> Inbound inquiries</div>
                <h3 className="font-display font-black text-2xl tracking-tighter">{leads.length} {leads.length === 1 ? "lead" : "leads"}</h3>
              </div>
            </div>
            {leads.length === 0 ? (
              <div className="text-sm text-[#52525B] border border-dashed border-[#E2E4E9] p-6 text-center">No inquiries yet. Publish the page, share the URL, and they'll show up here.</div>
            ) : (
              <div className="space-y-2">
                {leads.map((l) => (
                  <div key={l.lead_id} className="border border-[#E2E4E9] p-3" data-testid={`lead-${l.lead_id}`}>
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="font-semibold">{l.name || l.email}</div>
                      <a href={`mailto:${l.email}`} className="text-xs mono text-[#002FA7] hover:underline">{l.email}</a>
                      <div className="text-xs mono text-[#A1A1AA]">{l.created_at?.slice(0, 16).replace("T", " ")}</div>
                    </div>
                    {l.project_budget && <div className="text-xs text-[#52525B] mt-1">Budget: {l.project_budget}</div>}
                    <div className="text-sm mt-2 leading-relaxed whitespace-pre-wrap">{l.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <button onClick={() => onChange(!checked)} data-testid={testid} className={`w-9 h-5 border border-black flex items-center transition-all ${checked ? "bg-black justify-end" : "bg-white justify-start"}`}>
        <div className="w-4 h-4 bg-[#F59E0B] mx-0.5"/>
      </button>
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-[#52525B]">{hint}</div>
      </div>
    </label>
  );
}

function DraftCopyHelper({ profile, onPick }) {
  const [variants, setVariants] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [style, setStyle] = React.useState("");

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.draftMogulCopy(profile.services || [], style);
      setVariants(r.variants || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="border border-[#F59E0B] bg-[#FFFBEB] p-4 space-y-3" data-testid="draft-copy-helper">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#B45309]"/>
          <span className="text-eyebrow text-[#B45309]">AI copy assistant</span>
        </div>
        <div className="flex items-center gap-2">
          <input value={style} onChange={(e) => setStyle(e.target.value)} placeholder="Optional voice direction (e.g. 'no-bs, premium')" className="border border-[#B45309] p-1.5 text-xs mono w-72" data-testid="draft-style-input"/>
          <button onClick={generate} disabled={loading} data-testid="generate-drafts-button" className="bg-[#B45309] text-white px-3 py-1.5 text-xs hover:bg-black disabled:opacity-50 flex items-center gap-1">
            {loading ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>} {variants.length ? "Regenerate" : "Draft 3 variants"}
          </button>
        </div>
      </div>
      {variants.length > 0 && (
        <div className="space-y-2">
          {variants.map((v, i) => (
            <button key={i} onClick={() => onPick(v.tagline, v.bio)} data-testid={`draft-variant-${i}`} className="w-full text-left border border-black p-3 bg-white hover:bg-[#FFFBEB] transition-colors">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-display font-bold text-base">{v.tagline}</span>
                <span className="text-xs mono text-[#A1A1AA]">{v.voice}</span>
              </div>
              <div className="text-xs text-[#52525B] mt-2 leading-relaxed">{v.bio}</div>
              <div className="text-eyebrow text-[#059669] mt-2">Click to use this →</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
